import { BASE_REST_API_URL } from 'common/constants';
import type { BaseAPIParams } from 'lib/utils/BaseAPIParams';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import { buildQuery } from '@enmon/commons/lib/tools/utils/buildQuery';
import type {
  CreatePersonDto,
  SerializedPerson,
  SerializedPersonWithPhotoUrl,
  UpdatePersonDto,
} from '@enmon/commons/lib/tools';
import type { UploadFile } from 'antd';
import { BaseApiClient } from './baseApiClient';
import { parseSort } from '../utils/parseSort';

export type PersonsFilter = BaseAPIParams<SerializedPerson> & {
  sort?: string;
  persons?: SerializedPerson['id'][];
};
export type DeletePersonArg = SerializedPerson['id'];
export type CreatePersonArg = CreatePersonDto & { photo?: UploadFile<File> };
export type UpdatePersonArg = {
  id: SerializedPerson['id'];
  values: UpdatePersonDto & { photo?: UploadFile<File> };
};

export type FindPersonsResult = StrapiApiResponse<SerializedPersonWithPhotoUrl>;
export type DeletePersonResult = void;
export type CreatePersonResult = SerializedPerson;
export type UpdatePersonResult = SerializedPerson;

export class PersonsApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('persons', BASE_REST_API_URL, jwt);
  }

  public async find(filter: PersonsFilter, signal: AbortSignal | undefined): Promise<FindPersonsResult> {
    const { fields, persons, page, pageSize, populate, sort } = filter;
    const parsedSort = parseSort(sort);

    const response = await this.axios.get<FindPersonsResult>('', {
      params: buildQuery<SerializedPerson>({
        sort: parsedSort,
        pagination: {
          page,
          pageSize,
        },
        filters: {
          id: {
            $in: persons,
          },
        },
        fields,
        populate,
      }),
      signal,
    });

    return response.data;
  }

  public async create(value: CreatePersonArg): Promise<CreatePersonResult> {
    const { photo, ...restValues } = value;
    const formData = new FormData();
    formData.append('data', JSON.stringify(restValues));

    if (photo?.originFileObj) {
      formData.append('files.photo', photo.originFileObj);
    }

    const response = await this.axios.post<CreatePersonResult>('', formData);

    return response.data;
  }

  public async update({ id, values }: UpdatePersonArg): Promise<UpdatePersonResult> {
    const { photo, ...restValues } = values;
    const formData = new FormData();
    formData.append('data', JSON.stringify(restValues));

    if (photo?.originFileObj) {
      formData.append('files.photo', photo.originFileObj);
    }

    const response = await this.axios.put<UpdatePersonResult>(`${id}`, formData);

    return response.data;
  }

  public async delete(id: DeletePersonArg): Promise<DeletePersonResult> {
    const response = await this.axios.delete<DeletePersonResult>(`${id}`);

    return response.data;
  }
}
